import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ApiError,
  getEnvelope, signedUrl, documentUrl, certificateUrl, saveEnvelopeAsTemplate, reissueLink,
  remindNow, voidEnvelope,
  type AuditEvent,
  type EnvelopeDetail as Detail, type EnvelopeDocument,
  type Delivery,
  type Participation, type ParticipationStatus,
  type ReissueResult, type RemindResult,
} from '../api'
import { bytes, dateTime, eventLabel, relativeDays, retention, shortHash } from '../format'
import { signerFieldPrompt } from '../fields'
import { ConfirmSheet } from '../components/ConfirmSheet'
import { Fingerprint } from '../components/Fingerprint'
import { canReissue } from '../envelopeActions'
import { RETENTION_STARTS } from '../keepFor'
import { CERTIFICATE_LATER } from '../certificateWords'
import { nameAndAddress } from '../people'
import {
  crossPersonRuleWords, ENVELOPE_CROSS_PERSON_REMEDY, envelopeCrossPersonRules, refusedRuleWords, SAVE_TEMPLATE_INTRO,
} from '../templateRules'

/**
 * sends is whether this person can send at all: they hold a membership. A
 * person with no organization was offered Send again, which opened a draft
 * whose Send stayed off with nothing to choose (review-2-app R4).
 */
export function EnvelopeDetail({ sends = true }: { sends?: boolean }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<Detail | null>(null)
  // What stopped the page loading, kept whole rather than as its sentence:
  // which kind of failure it was decides what the page says.
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  // What a reissue came to, kept per participation: the new link to hand on, or
  // what went wrong. Sending it again is the remedy for a link that reached the
  // wrong person or never arrived, so the sender has to be told which happened.
  const [reissued, setReissued] = useState<Record<string, ReissueResult>>({})
  // Per row, like its two neighbours. A single id here would mean starting a
  // second signer's reissue re-enables the first one's button while its request
  // is still open — a control rendered idle for an action that is running, and
  // an invitation to click it again. The second click withdraws the link the
  // first one just minted and emailed.
  const [reissuing, setReissuing] = useState<Record<string, boolean>>({})
  const [reissueError, setReissueError] = useState<Record<string, string>>({})
  // A reminder, likewise per row: what went out, or why it did not. A
  // reminder never withdraws anything, so unlike a reissue there is no link
  // to hand on afterwards, only the fact that the message went.
  const [reminded, setReminded] = useState<Record<string, RemindResult>>({})
  const [reminding, setReminding] = useState<Record<string, boolean>>({})
  const [remindError, setRemindError] = useState<Record<string, string>>({})

  async function remind(participationId: string) {
    setReminding((prev) => ({ ...prev, [participationId]: true }))
    setRemindError((prev) => ({ ...prev, [participationId]: '' }))
    setReminded((prev) => {
      const next = { ...prev }
      delete next[participationId]
      return next
    })
    try {
      const result = await remindNow(id, participationId)
      setReminded((prev) => ({ ...prev, [participationId]: result }))
      setDetail(await getEnvelope(id))
    } catch (err) {
      setRemindError((prev) => ({
        ...prev,
        [participationId]: err instanceof Error ? err.message : 'Could not send that reminder.',
      }))
    } finally {
      setReminding((prev) => {
        const next = { ...prev }
        delete next[participationId]
        return next
      })
    }
  }

  async function reissue(participationId: string) {
    setReissuing((prev) => ({ ...prev, [participationId]: true }))
    setReissueError((prev) => ({ ...prev, [participationId]: '' }))
    // The previous attempt's answer goes too. The moment the server commits the
    // new link, the URL from last time is withdrawn, so leaving it on screen
    // under "New link, to hand on yourself" is a live false claim — and if this
    // attempt then fails, it stays there beside the error indefinitely.
    setReissued((prev) => {
      const next = { ...prev }
      delete next[participationId]
      return next
    })
    try {
      const result = await reissueLink(id, participationId)
      setReissued((prev) => ({ ...prev, [participationId]: result }))
      setDetail(await getEnvelope(id))
    } catch (err) {
      // It ended since this page loaded (signed, declined or withdrawn
      // elsewhere): the server's sentence says so, and the page reads the
      // envelope again so it stops offering what it cannot do.
      if (err instanceof ApiError && err.code === 'envelope_closed') {
        void getEnvelope(id).then(setDetail).catch(() => {})
      }
      setReissueError((prev) => ({
        ...prev,
        [participationId]: err instanceof Error ? err.message : 'Could not reissue that link.',
      }))
    } finally {
      setReissuing((prev) => {
        const next = { ...prev }
        delete next[participationId]
        return next
      })
    }
  }

  const load = useCallback(() => {
    getEnvelope(id)
      .then(setDetail)
      .catch((e: unknown) => setError(e))
  }, [id])
  // Not `useEffect(load, [load])`: load returns a promise, React takes an
  // effect's return value as its cleanup function, and the whole page stopped
  // responding to state — no dialog would open, including ones that had worked
  // for months. It typechecks and it is silent.
  useEffect(() => {
    load()
  }, [load])

  if (error !== null) {
    return (
      <LoadProblem
        error={error}
        onRetry={() => {
          setError(null)
          load()
        }}
      />
    )
  }
  if (!detail) return <main className="page"><p className="muted">Loading…</p></main>

  const { envelope: e, fields, audit, chain, routing } = detail
  const skipped = new Set(routing?.skipped ?? [])
  const signed = e.status === 'completed'
  // The sender is on the roster too, because what they did is part of the
  // record. This card is about everyone who was invited to act.
  const parties = detail.participations.filter((p) => p.role !== 'sender')
  const signers = parties.filter((p) => p.role === 'signer' || p.role === 'witness')
  const signingSlots = uniqueSigningSlots(signers)
  const ordered = parties.some((r) => r.routing_order > 1)
  // Only the people the envelope is actually waiting on, as the server says
  // from the chain: the same answer the signing link itself runs on. The page
  // used to guess from the roster order, and a guess here sends the sender to
  // chase somebody whose turn has not come, or who was never asked to act.
  const blocking = parties.find((r) => r.waiting_on)
  const signedSlots = signingSlots.filter((r) => slotSigned(r, parties)).length
  const fieldsFilled = fields.filter((f) => f.filled_at).length
  const activity = activityFeed(audit, parties)

  return (
    <EnvelopeDetailView
      sends={sends}
      id={id}
      detail={detail}
      e={e}
      fields={fields}
      audit={audit}
      chain={chain}
      parties={parties}
      signers={signers}
      signingSlots={signingSlots}
      ordered={ordered}
      skipped={skipped}
      signed={signed}
      signedSlots={signedSlots}
      fieldsFilled={fieldsFilled}
      blocking={blocking}
      activity={activity}
      reissued={reissued}
      reissuing={reissuing}
      reissueError={reissueError}
      reissue={reissue}
      reminded={reminded}
      reminding={reminding}
      remindError={remindError}
      remind={remind}
      saving={saving}
      withdrawing={withdrawing}
      setSaving={setSaving}
      setWithdrawing={setWithdrawing}
      onWithdrawn={load}
      navigate={navigate}
    />
  )
}

/**
 * Why the document could not be shown, told apart.
 *
 * Every failure used to read "Not found", so a sender with no signal, or one
 * meeting a server that had failed, was told their document was gone. Only
 * the server's own answer that there is no such document says that: the read
 * is scoped, so it is the same answer for one that does not exist and one this
 * person cannot see. A server that answered with a sentence of its own is
 * shown with that sentence; no answer at all, or one that is not the API's,
 * is the sentence for an unreachable server, as the signing page and the
 * shell say it. Both can be asked again.
 */
export function LoadProblem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const refused = error instanceof ApiError && error.code !== 'unknown' ? error : null
  if (refused?.code === 'not_found') {
    return (
      <main className="page page-narrow">
        <div id="envelope-not-found" className="card empty">
          <h2>Not found</h2>
          <p className="muted">This document does not exist, or it is not one you can see.</p>
          <Link className="btn btn-secondary" to="/">Back to envelopes</Link>
        </div>
      </main>
    )
  }
  return (
    <main className="page page-narrow">
      <div id="envelope-unreachable" className="card empty">
        <h2>{refused ? 'docsdone could not answer' : 'Could not reach docsdone'}</h2>
        <p className="muted">
          {refused ? refused.message : 'Check your connection and try again.'}
        </p>
        <button id="try-again" className="btn" onClick={onRetry}>Try again</button>
      </div>
    </main>
  )
}

export function EnvelopeDetailView({
  id: _id, detail, e, fields, audit, chain, parties, signers, signingSlots, ordered, skipped,
  signed, signedSlots, fieldsFilled, blocking, activity,
  reissued, reissuing, reissueError, reissue,
  reminded, reminding, remindError, remind,
  saving, withdrawing,
  setSaving, setWithdrawing, onWithdrawn, navigate, sends = true,
}: {
  id: string
  detail: Detail
  e: Detail['envelope']
  fields: Detail['fields']
  audit: Detail['audit']
  chain: Detail['chain']
  parties: Participation[]
  signers: Participation[]
  signingSlots: Participation[]
  ordered: boolean
  skipped: Set<string>
  signed: boolean
  signedSlots: number
  fieldsFilled: number
  blocking: Participation | undefined
  activity: { seq: number; title: string; detail?: string; occurred_at: string; sealed?: true }[]
  reissued: Record<string, ReissueResult>
  reissuing: Record<string, boolean>
  reissueError: Record<string, string>
  reissue: (participationId: string) => void
  reminded: Record<string, RemindResult>
  reminding: Record<string, boolean>
  remindError: Record<string, string>
  remind: (participationId: string) => void
  saving: boolean
  withdrawing: boolean
  setSaving: (v: boolean) => void
  setWithdrawing: (v: boolean) => void
  onWithdrawn: () => void
  navigate: ReturnType<typeof useNavigate>
  /** Whether the person holds a membership, so Send again has somewhere to send from. */
  sends?: boolean
}) {
  // The retention sweep deleted the files (purged_at, document_key NULL), so
  // every act that reads them answers 404. Send again ended on the upload
  // step with "Could not read the original document.", which reads as a
  // fault (review 6, docs-6). The page now offers none of them, as the app
  // does (ActionBar planActs), and says why, in the app's words.
  const purged = Boolean(e.purged_at)
  return (
    <main className="page">
      <div className="page-head">
        <div className="page-label">Document</div>
        <div className="row-between">
          <div>
            <h1>{e.title}</h1>
            <p>
              <span className={`badge badge-${e.status}`}>{e.status}</span>
              <span className="muted small" style={{ marginLeft: '.75rem' }}>
                Created {dateTime(e.created_at)}
                {e.sent_by && ` by ${nameAndAddress(e.sent_by.name, e.sent_by.identifier, '()')}`}
              </span>
            </p>
          </div>
          <div className="row">
            {!purged && detail.documents.length <= 1 && (
              <a className="btn btn-secondary" href={documentUrl(e.id)} target="_blank" rel="noreferrer">
                View original
              </a>
            )}
            {!purged && signed && detail.documents.length === 1 && (
              <a className="btn" href={signedUrl(e.id)}>Download signed PDF</a>
            )}
            {e.certificate_ready && (
              <a className="btn btn-secondary" href={certificateUrl(e.id)}>Certificate</a>
            )}
            {/* Only while the document is out: a withdrawn or declined one is
                never finished, and the certificate is issued only when it is
                (completeEnvelopeTx), so the line promised evidence that cannot
                exist (review 5, web-2; the app's certificateLater). */}
            {e.status === 'sent' && !e.certificate_ready && (
              <span className="small faint">{CERTIFICATE_LATER}</span>
            )}
          </div>
        </div>
        {purged && <div id="envelope-purged" className="notice" style={{ marginTop: '.75rem' }}>{PURGED}</div>}
      </div>

      <div className="grid-doc grid-doc-detail">
        <div className="stack">
          {detail.documents.length > 1 && (
            <div className="card">
              <h2>Documents</h2>
              <p className="small muted">
                {detail.documents.length} documents were sent as one envelope. Each document is sealed on its own, including the entries made on it.
              </p>
              <ul className="doc-list">
                {detail.documents.map((d) => (
                  <li key={d.id} className="row-between doc-list-row">
                    <span>
                      <strong>{d.position === 0 ? e.title : d.name}</strong>
                      <span className="small faint"> · {d.page_count} pages · {bytes(d.document_bytes)}</span>
                    </span>
                    {!purged && <span className="row">
                      <a
                        className="btn btn-ghost btn-small"
                        href={documentUrl(e.id, d.position)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Original
                      </a>
                      {/* Offered per document, because a signer with no box on
                          one of them never sealed it and there is nothing to
                          download. Saying so is more use than a button that
                          answers 409. */}
                      {d.signed_sha256 ? (
                        <a className="btn btn-small" href={signedUrl(e.id, d.position)}>Signed</a>
                      ) : (
                        <span className="small faint">Not signed yet</span>
                      )}
                    </span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card">
            <div className="card-head-inline">
              <h2>Progress</h2>
              {signingSlots.length > 1 && (
                <span className="small faint">{signedSlots} of {signingSlots.length} signed</span>
              )}
            </div>
            <p className="progress-headline">{progressHeadline(e.status, signed, blocking, ordered)}</p>
            {signingSlots.length > 1 && (
              <div className="progress-track" aria-hidden>
                <div
                  className={`progress-fill${signed ? ' is-done' : ''}`}
                  style={{ width: `${signingSlots.length ? (signedSlots / signingSlots.length) * 100 : 0}%` }}
                />
              </div>
            )}
            <ol className="party">
              {signingSlots.map((r) => {
                const slot = slotProgress(r, parties, e.status)
                const done = slot.mark === 'signed'
                const declined = slot.mark === 'declined'
                return (
                  <li
                    key={r.id}
                    className={done ? 'is-signed' : declined ? 'is-declined' : undefined}
                  >
                    <span className="party-mark" aria-hidden>
                      {done ? '✓' : ordered ? r.routing_order : '·'}
                    </span>
                    <span className="party-name">{recipientLabel(r, parties)}</span>
                    <span className="small faint">{slot.word}</span>
                  </li>
                )
              })}
            </ol>
          </div>

          <div className="card card-tight">
            <h3>Recipients</h3>
            {!signed && e.status !== 'declined' && blocking && (
              <div className="notice" style={{ marginBottom: '.75rem' }}>
                Waiting on <strong>{blocking.name}</strong>
                {ordered && (blocking.role === 'signer' || blocking.role === 'witness')
                  ? ` (signs ${ordinal(blocking.routing_order)})`
                  : ordered && blocking.role === 'needs_to_view'
                    ? ` (has to open it, ${ordinal(blocking.routing_order)} in order)`
                    : ''}
                .
              </div>
            )}
            <div className="table-wrap">
              <table className="table recipients-table">
                <tbody>
                  {parties.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <strong>
                          {ordered && <span className="faint small">{r.routing_order}. </span>}
                          {recipientLabel(r, parties)}
                        </strong>
                        <div className="faint small">{r.identifier}</div>
                        <div className="faint small">{roleLabel(r.role)}</div>
                        {r.state?.decline_reason && (
                          <div className="small" style={{ color: 'var(--bad)' }}>{r.state.decline_reason}</div>
                        )}
                        {signers.length > 1 && r.state?.signed_document_sha256 && (
                          <div className="faint small mono" title="The document as this signer sealed it">
                            <Fingerprint hash={shortHash(r.state.signed_document_sha256)} />
                          </div>
                        )}
                      </td>
                      <td className="recipient-status-cell">
                        <RecipientStatusRail
                          p={r}
                          parties={parties}
                          skipped={skipped.has(r.id)}
                          envelopeStatus={e.status}
                          reissuing={!!reissuing[r.id]}
                          reissueError={reissueError[r.id]}
                          reissued={reissued[r.id]}
                          onReissue={() => void reissue(r.id)}
                          reminding={!!reminding[r.id]}
                          remindError={remindError[r.id]}
                          reminded={reminded[r.id]}
                          onRemind={() => void remind(r.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {activity.length > 0 && (
            <div className="card">
              <div className="card-head-inline">
                <h2>Activity</h2>
                <span className="small faint">{events(activity.length)}</span>
              </div>
              <ul className="activity-list">
                {activity.map((item) => (
                  <li key={item.seq}>
                    <span className={item.sealed ? 'dot is-sealed' : 'dot'} />
                    <div>
                      <div className="evt-type">{item.title}</div>
                      <div className="evt-meta">
                        {dateTime(item.occurred_at)}
                        {item.detail && ` · ${item.detail}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <details className="card detail-panel">
            <summary>Audit trail ({events(audit.length)})</summary>
            {chain.valid ? (
              <div className="notice notice-good" style={{ marginBottom: '1rem' }}>
                <strong>Chain intact.</strong> {chainIntact(chain.event_count)}
              </div>
            ) : (
              <div className="notice notice-bad" style={{ marginBottom: '1rem' }}>
                <strong>Chain broken.</strong> {chain.reason}
              </div>
            )}
            <ul className="timeline">
              {audit.map((ev) => (
                <li key={ev.seq}>
                  <span className={sealsTheDocument(ev.type) ? 'dot is-sealed' : 'dot'} />
                  <div>
                    <div className="evt-type">{eventLabel(ev.type)}</div>
                    <div className="evt-meta">
                      {dateTime(ev.occurred_at)}
                      {(ev.name_presented || ev.identifier_presented) &&
                        ` · ${nameAndAddress(ev.name_presented, ev.identifier_presented, '<>')}`}
                      {` · ${ev.auth_method}`}
                      {ev.ip && ` · ${ev.ip}`}
                    </div>
                    {ev.data && Object.keys(ev.data).length > 0 && (
                      <details className="evt-details">
                        <summary>Event data</summary>
                        <div className="faint small mono">
                          {Object.entries(ev.data).map(([k, v]) => `${k}=${eventValue(v)}`).join('  ')}
                        </div>
                      </details>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        </div>

        <div className="stack">
          <div className="card card-tight">
            <h3>Next steps</h3>
            <ul className="next-steps">
              {/* Chasing comes first, because it is what the sender opened
                  this page to do. Reissuing is the remedy for a link that
                  went wrong, and it is offered below rather than instead. */}
              {!signed && blocking && canRemind(blocking) && (
                <li>
                  <button
                    id={`remind-${blocking.id}`}
                    className="btn btn-block"
                    disabled={!!reminding[blocking.id]}
                    onClick={() => void remind(blocking.id)}
                  >
                    {reminding[blocking.id] ? 'Sending…' : `Remind ${blocking.name}`}
                  </button>
                </li>
              )}
              {!signed && blocking && canReissue(blocking, isClosed(blocking, parties), e.status) && (
                <li>
                  <button
                    className={`btn btn-block${canRemind(blocking) ? ' btn-secondary' : ''}`}
                    disabled={!!reissuing[blocking.id]}
                    onClick={() => void reissue(blocking.id)}
                  >
                    {reissuing[blocking.id] ? 'Reissuing…' : `Reissue link for ${blocking.name}`}
                  </button>
                </li>
              )}
              {!purged && signed && detail.documents.length === 1 && (
                <li><a className="btn btn-block" href={signedUrl(e.id)}>Download signed PDF</a></li>
              )}
              {e.certificate_ready && (
                <li><a className="btn btn-secondary btn-block" href={certificateUrl(e.id)}>Download certificate</a></li>
              )}
              {sends && !purged && (
                <li>
                  <Link className="btn btn-secondary btn-block" to={`/new?from=${e.id}`}>
                    Send again
                  </Link>
                </li>
              )}
              {!purged && (
                <li>
                  <button className="btn btn-secondary btn-block" onClick={() => setSaving(true)}>
                    Save as template
                  </button>
                </li>
              )}
              {e.status === 'sent' && (
                <li className="next-steps-foot">
                  <button className="btn btn-danger btn-block" onClick={() => setWithdrawing(true)}>
                    Withdraw
                  </button>
                </li>
              )}
            </ul>
          </div>

          <div className="card card-tight">
            <div className="card-head-inline">
              <h3>{detail.documents.length > 1 ? 'Envelope' : 'Document'}</h3>
              {!purged && detail.documents.length <= 1 && (
                <a className="btn btn-secondary btn-small" href={documentUrl(e.id)} target="_blank" rel="noreferrer">
                  Original
                </a>
              )}
            </div>
            {detail.documents.length > 1 ? (
              <>
                <div className="doc-kv"><span className="muted">Documents</span><span>{detail.documents.length}</span></div>
                <div className="doc-kv">
                  <span className="muted">Pages</span>
                  <span>{detail.documents.reduce((n, d) => n + d.page_count, 0)}</span>
                </div>
                <div className="doc-kv">
                  <span className="muted">Original</span>
                  <span>{bytes(detail.documents.reduce((n, d) => n + d.document_bytes, 0))}</span>
                </div>
              </>
            ) : (
              <>
                <div className="doc-kv"><span className="muted">Pages</span><span>{e.page_count}</span></div>
                <div className="doc-kv"><span className="muted">Original</span><span>{bytes(e.document_bytes)}</span></div>
                {signed && <div className="doc-kv"><span className="muted">Signed</span><span>{bytes(e.signed_bytes)}</span></div>}
              </>
            )}
            <div className="doc-kv">
              <span className="muted">Fields</span>
              <span>{fieldsFilled} of {fields.length} filled</span>
            </div>
            <div className="doc-kv">
              <span className="muted">Retention</span>
              {endedUnfinished(e.status) ? (
                <div className="doc-kv-value">
                  {retention(e.retention_days)}
                  <div className="faint small">{RETENTION_NOT_STARTED}</div>
                </div>
              ) : (
                <span>{retention(e.retention_days)} after it is finished</span>
              )}
            </div>
          </div>

          {fields.length > 0 && (
            <details className="card card-tight detail-panel">
              <summary>Fields ({fields.length})</summary>
              <table className="table">
                <tbody>
                  {fields.map((f) => (
                    <tr key={f.id}>
                      <td className="muted">
                        {signerFieldPrompt(f.label ?? '', f.kind)}
                        {signers.length > 1 && (
                          <div className="faint small">
                            {signers.find((r) => r.id === f.participation_id)?.name}
                          </div>
                        )}
                      </td>
                      <td>
                        {f.filled_at && f.value_text
                          ? <span>{f.value_text}</span>
                          : (
                            <span className="faint">
                              {documentOf(detail.documents, f.envelope_item_id, e.title)}
                              page {f.page} · {fieldWord(Boolean(f.filled_at), e.status)}
                            </span>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          <details className="card card-tight detail-panel">
            <summary>Technical details</summary>
            <table className="table">
              <tbody>
                {detail.documents.length <= 1 && (
                  <tr>
                    <td className="muted">Fingerprint</td>
                    <td className="mono"><Fingerprint hash={shortHash(signed ? e.signed_sha256 : e.document_sha256)} /></td>
                  </tr>
                )}
                <tr>
                  <td className="muted">Expires</td>
                  <td>
                    {e.expires_at ? (
                      <>{dateTime(e.expires_at)}<div className="faint small">{relativeDays(e.expires_at)}</div></>
                    ) : (
                      // The store sets expires_at only when it finishes the
                      // document (completeEnvelopeTx). A withdrawn or declined
                      // document is never finished, so no date will come, and
                      // "starts when it is finished" promised one (review 4,
                      // app request 6; the app's words since 7dd8a7b).
                      <span className="faint">
                        {endedUnfinished(e.status)
                          ? 'no date, as it will not be finished'
                          : 'starts when it is finished'}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="muted">Automatic reminders</td>
                  <RemindersCell status={e.status} enabled={e.reminders_enabled} />
                </tr>
              </tbody>
            </table>
            <p className="small faint" style={{ marginBottom: 0 }}>
              {endedUnfinished(e.status) ? RETENTION_NEVER_STARTS : RETENTION_STARTS}
            </p>
          </details>
        </div>
      </div>

      {withdrawing && (
        <Withdraw
          id={e.id}
          title={e.title}
          onDone={() => {
            setWithdrawing(false)
            onWithdrawn()
          }}
          onClose={() => setWithdrawing(false)}
        />
      )}

      {saving && (
        <SaveAsTemplate
          detail={detail}
          onClose={() => setSaving(false)}
          onSaved={() => navigate('/templates')}
        />
      )}
    </main>
  )
}

/**
 * A document that has ended without being finished: withdrawn, or declined by
 * a signer. The store finishes a document only in completeEnvelopeTx, which
 * sets the retention date and issues the certificate, so on these neither
 * comes. Every line of the page that waits for "finished" asks this first
 * (review 5, design-2).
 */
export function endedUnfinished(status: string): boolean {
  return status === 'voided' || status === 'declined'
}

/**
 * The Progress row's word for a person who has not finished on a document that
 * ended without everyone: "withdrawn" once the sender withdrew it, "closed"
 * once somebody declined it. These are the app's words for the same row
 * (mobile envelope model, endedWord), so one product says one thing; the web
 * said "not signed" for both (R7-web). null while the document is out, and
 * once it is finished.
 */
export function endedWord(status: string): 'withdrawn' | 'closed' | null {
  if (status === 'voided') return 'withdrawn'
  if (status === 'declined') return 'closed'
  return null
}

/** A count of events, singular for one: every just-sent document has one, and read "1 events". */
export function events(n: number): string {
  return `${n} ${n === 1 ? 'event' : 'events'}`
}

/**
 * What an intact chain is said to hold. One event is not linked to one
 * before it, so for one the sentence does not say that each is (review 6,
 * design-5).
 */
export function chainIntact(n: number): string {
  if (n === 1) return '1 event. Nothing has been edited or removed.'
  return `${n} events, each cryptographically linked to the one before it. Nothing has been edited or removed.`
}

/**
 * The Fields panel's word for one box: "signed" once filled, and while it is
 * not, "awaiting" only while the document is out. On a withdrawn or declined
 * document no box will ever be filled, so it says the Progress row's word,
 * "withdrawn" or "closed" (review 6, design-4).
 */
export function fieldWord(filled: boolean, status: string): string {
  if (filled) return 'signed'
  return endedWord(status) ?? 'awaiting'
}

/**
 * The Automatic reminders row. The store reminds only while the document is
 * out (reminders.go: status sent, both for Remind now and the schedule), so
 * "On, 1, 3 and 7 days" on a withdrawn, declined or finished document stated
 * a schedule that will never run (review 5, design-4). The setting is still
 * on; the row says why nothing goes out.
 */
export function remindersRow(status: string, enabled: boolean): { state: string; detail: string } {
  if (!enabled) return { state: 'Off', detail: '' }
  if (status === 'voided') return { state: 'Off', detail: 'None go out, as the document was withdrawn' }
  if (status === 'declined') return { state: 'Off', detail: 'None go out, as the document was declined' }
  if (status === 'completed') return { state: 'Off', detail: 'None go out, as the document is finished' }
  return { state: 'On', detail: '1, 3 and 7 days after each link went out' }
}

function RemindersCell({ status, enabled }: { status: string; enabled: boolean }) {
  const row = remindersRow(status, enabled)
  return (
    <td>
      {row.state}
      {row.detail && <div className="faint small">{row.detail}</div>}
    </td>
  )
}

/** The app's sentence for the same state (mobile envelope words, purged). */
const PURGED = 'The files were deleted at the end of the retention period.'

const RETENTION_NOT_STARTED ='not started, as it will not be finished'
const RETENTION_NEVER_STARTS =
  'This document will not be finished, so its retention period does not start.'

/** Dev-only fixture for /preview/envelope-detail — two signers, first signed. */
export function envelopeDetailPreviewFixture(): Detail {
  const expires = new Date(Date.now() + 6 * 86_400_000).toISOString()
  const adaSigned = '2025-09-14T12:06:00.000000000Z'
  const reminded = '2025-09-15T12:00:00.000000000Z'
  return {
    envelope: {
      id: 'env_preview',
      organization_id: 'org_preview',
      title: 'Residential tenancy agreement',
      status: 'sent',
      created_at: '2025-09-14T11:59:00.000000000Z',
      document_sha256: '836111d0a4f2e8b91c4d7e2f1a903856c11d0a4f2e8b91c4d7e2f',
      document_bytes: 715_264,
      page_count: 3,
      retention_days: 2555,
      legal_hold: false,
      reminders_enabled: true,
      certificate_ready: false,
      sent_by: { name: 'docsclone service', identifier: 'service:bootstrap', method: 'service_credential' },
      updated_at: '2025-09-14T12:06:00.000000000Z',
    },
    documents: [{
      id: 'doc_preview',
      envelope_id: 'env_preview',
      position: 0,
      name: 'Residential tenancy agreement',
      document_sha256: '836111d0a4f2e8b91c4d7e2f1a903856c11d0a4f2e8b91c4d7e2f',
      document_bytes: 715_264,
      page_count: 3,
    }],
    participations: [
      {
        id: 'part_ada', envelope_id: 'env_preview', role: 'signer', routing_order: 1, position: 0,
        name: 'Ada Lovelace', identifier_kind: 'email', identifier: 'ada@example.com',
        identifier_origin: 'sender', created_at: '2025-09-14T11:59:00.000000000Z',
        state: { status: 'signed', signed_at: adaSigned },
        link: { delivery: 'emailed_by_docsdone', expires_at: expires, revoked: false },
        waiting_on: false,
        reminders: { sent: 0 },
      },
      {
        id: 'part_loy', envelope_id: 'env_preview', role: 'signer', routing_order: 2, position: 1,
        name: 'Loy Chen', identifier_kind: 'email', identifier: 'loy@example.com',
        identifier_origin: 'sender', created_at: '2025-09-14T11:59:00.000000000Z',
        state: { status: 'pending' },
        link: { delivery: 'emailed_by_docsdone', expires_at: expires, revoked: false },
        waiting_on: true,
        reminders: { sent: 1, last_at: reminded, next_at: new Date(Date.now() + 2 * 86_400_000).toISOString() },
      },
    ],
    fields: [
      { id: 'f1', envelope_id: 'env_preview', envelope_item_id: 'doc_preview', participation_id: 'part_ada', kind: 'signature', page: 1, norm_x: 0.1, norm_y: 0.7, norm_w: 0.3, norm_h: 0.08, required: true, value_text: 'Signed', filled_at: adaSigned },
      { id: 'f2', envelope_id: 'env_preview', envelope_item_id: 'doc_preview', participation_id: 'part_loy', kind: 'signature', page: 3, norm_x: 0.5, norm_y: 0.7, norm_w: 0.3, norm_h: 0.08, required: true },
      { id: 'f3', envelope_id: 'env_preview', envelope_item_id: 'doc_preview', participation_id: 'part_ada', kind: 'date_signed', page: 1, norm_x: 0.1, norm_y: 0.8, norm_w: 0.2, norm_h: 0.05, required: true, value_text: '14 Sep 2025', filled_at: adaSigned },
      { id: 'f4', envelope_id: 'env_preview', envelope_item_id: 'doc_preview', participation_id: 'part_loy', kind: 'date_signed', page: 3, norm_x: 0.5, norm_y: 0.8, norm_w: 0.2, norm_h: 0.05, required: true },
      { id: 'f5', envelope_id: 'env_preview', envelope_item_id: 'doc_preview', participation_id: 'part_ada', kind: 'initials', page: 1, norm_x: 0.1, norm_y: 0.6, norm_w: 0.1, norm_h: 0.05, required: true, value_text: 'AL', filled_at: adaSigned },
      { id: 'f6', envelope_id: 'env_preview', envelope_item_id: 'doc_preview', participation_id: 'part_loy', kind: 'dropdown', label: 'Amount', page: 1, norm_x: 0.5, norm_y: 0.5, norm_w: 0.2, norm_h: 0.05, required: true },
    ],
    audit: [
      { seq: 7, format_version: 1, kind: 'system', type: 'reminder.sent', auth_method: 'none', verifier: 'none', evidence: {}, data: { participation_id: 'part_loy', trigger: 'schedule', slot: 1 }, occurred_at: reminded, hash: 'a1c3', prev_hash: 'e4b7' },
      { seq: 6, format_version: 1, kind: 'act', type: 'participant.signed', auth_method: 'email_link', verifier: 'docsdone', evidence: {}, data: {}, occurred_at: adaSigned, hash: 'e4b7', prev_hash: '9c01', name_presented: 'Ada Lovelace', identifier_presented: 'ada@example.com' },
      { seq: 5, format_version: 1, kind: 'act', type: 'participant.consented', auth_method: 'email_link', verifier: 'docsdone', evidence: {}, data: {}, occurred_at: '2025-09-14T12:05:00.000000000Z', hash: '9c01', prev_hash: '1f88', name_presented: 'Ada Lovelace', identifier_presented: 'ada@example.com' },
      { seq: 4, format_version: 1, kind: 'act', type: 'participant.viewed', auth_method: 'email_link', verifier: 'docsdone', evidence: {}, data: {}, occurred_at: '2025-09-14T12:04:00.000000000Z', hash: '1f88', prev_hash: '7aa2', name_presented: 'Ada Lovelace', identifier_presented: 'ada@example.com' },
      { seq: 3, format_version: 1, kind: 'act', type: 'participant.authenticated', auth_method: 'email_link', verifier: 'docsdone', evidence: {}, data: {}, occurred_at: '2025-09-14T12:04:00.000000000Z', hash: '7aa2', prev_hash: '55ce', name_presented: 'Ada Lovelace', identifier_presented: 'ada@example.com' },
      { seq: 2, format_version: 1, kind: 'act', type: 'envelope.sent', auth_method: 'service_credential', verifier: 'docsdone', evidence: {}, data: {}, occurred_at: '2025-09-14T11:59:00.000000000Z', hash: '55ce', prev_hash: 'c0ff', name_presented: 'docsclone service', identifier_presented: 'service:bootstrap' },
      { seq: 1, format_version: 1, kind: 'act', type: 'envelope.created', auth_method: 'service_credential', verifier: 'docsdone', evidence: {}, data: { document_sha256: '836111d0…', retention_days: 2555 }, occurred_at: '2025-09-14T11:59:00.000000000Z', hash: 'c0ff', prev_hash: 'genesis', name_presented: 'docsclone service', identifier_presented: 'service:bootstrap' },
    ],
    chain: { valid: true, event_count: 7 },
  }
}

/**
 * What the Save as template sheet says when the save is refused. A rule the
 * sheet did not find first gets the next step added to the server's sentence;
 * every other refusal is the server's own sentence, which is specific: 409
 * document_purged says retention deleted the documents, so nothing can be
 * saved from them (review-5-server request), and 409 cannot_be_a_template
 * names the signer with no signature box and says to send the document
 * again with one (review 6). The sheet stays open on a refusal.
 */
export function saveTemplateRefusal(e: unknown): string {
  if (e instanceof ApiError) return refusedRuleWords(e.code, e.message, ENVELOPE_CROSS_PERSON_REMEDY) ?? e.message
  return e instanceof Error && e.message ? e.message : 'Could not save that template.'
}

/**
 * SaveAsTemplate keeps an envelope's layout for next time.
 *
 * The roles come from the envelope itself, named after the people who signed
 * this one. Renaming them here is worth doing, because "Ada Lovelace" is a poor
 * name for a slot somebody else fills next month, but the save does not take
 * names yet, so it is left for when it does.
 */
function SaveAsTemplate({
  detail, onClose, onSaved,
}: {
  detail: Detail
  onClose: () => void
  onSaved: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState(detail.envelope.title)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // A document sent before b29a80f can hold a box that waits on another
  // signer's box, and the server refuses to save it as a template. It is
  // named here, before the press, with the way to a template that works
  // (templateRules.ts).
  const crossPerson = envelopeCrossPersonRules(detail.participations, detail.fields)
  const ready = name.trim() !== '' && crossPerson.length === 0

  // A <dialog> only enters the top layer — and only traps focus, makes the page
  // behind it inert, and gives focus back to whatever opened it — when it is
  // opened with showModal(). Rendering it does none of that. This component is
  // mounted only while the sheet is open, so opening it here is its whole
  // lifecycle; the cleanup matters because StrictMode runs the effect twice in
  // development and showModal() throws on an already-open dialog.
  useEffect(() => {
    const el = ref.current
    el?.showModal()
    return () => el?.close()
  }, [])

  async function save() {
    setBusy(true)
    setError('')
    try {
      await saveEnvelopeAsTemplate(detail.envelope.id, { name: name.trim() })
      onSaved()
    } catch (e) {
      setError(saveTemplateRefusal(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <dialog
      className="sheet"
      ref={ref}
      aria-label="Save as template"
      // Every way out goes through the dialog's own close(), and React only
      // unmounts once it has happened. Closing it in React first — on Escape,
      // say — tears the element out while it is still open, and the browser
      // then has nothing to give focus back to, so it lands on <body>.
      onClose={onClose}
    >
      <div className="sheet-body card">
        <h2>Save as template</h2>
        <p className="small muted">
          {SAVE_TEMPLATE_INTRO}
        </p>

        <div style={{ marginBottom: '.9rem' }}>
          <label htmlFor="tpl-name">Template name</label>
          <input id="tpl-name" type="text" value={name} onChange={(ev) => setName(ev.target.value)} />
        </div>

        <label>Roles</label>
        <p className="small faint" style={{ marginTop: 0 }}>
          One slot per signer on this envelope, named after them for now.
        </p>
        <div className="stack-tight" style={{ marginBottom: '1rem' }}>
          {templateRoles(detail.participations).map((r) => (
            <div key={r.key} className="faint small">{r.name}</div>
          ))}
        </div>

        {crossPerson.length > 0 && (
          <div id="save-template-cross-person" className="notice notice-bad" style={{ marginBottom: '1rem' }}>
            {crossPerson.map((r, i) => <p key={i}>{crossPersonRuleWords(r)}</p>)}
            <p>{ENVELOPE_CROSS_PERSON_REMEDY}</p>
          </div>
        )}

        {error && <div className="notice notice-bad">{error}</div>}

        <div className="row">
          <button className="btn" disabled={!ready || busy} onClick={save}>
            {busy ? <><span className="spinner" /> Saving…</> : 'Save template'}
          </button>
          <button className="btn btn-ghost" onClick={() => ref.current?.close()}>Cancel</button>
        </div>
      </div>
    </dialog>
  )
}

/**
 * The roles a template saved from this envelope will have, in the order the
 * server saves them (store rolesFromEnvelopeTx): signers by routing order,
 * position and id, and a signing group once, named as the group was. The
 * sheet listed each member of a group by name, while the saved template had
 * one role named after the group (review 6, carry-web-group-once). The app's
 * templateRoles (mobile envelope actions, Sheets.tsx) is this rule.
 */
export function templateRoles(participations: Participation[]): { key: string; name: string }[] {
  const signers = participations
    .filter((x) => x.role === 'signer')
    .slice()
    .sort((a, b) => a.routing_order - b.routing_order || a.position - b.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const out: { key: string; name: string }[] = []
  const groups = new Set<string>()
  for (const x of signers) {
    if (x.signing_group_id) {
      if (groups.has(x.signing_group_id)) continue
      groups.add(x.signing_group_id)
      out.push({ key: x.signing_group_id, name: x.signing_group_label || x.name })
    } else {
      out.push({ key: x.id, name: x.name })
    }
  }
  return out
}

/**
 * Where one participant has got to. The workflow row is absent until something
 * has happened to them, which reads as pending.
 */
function statusOf(p: Participation): ParticipationStatus {
  return p.state?.status ?? 'pending'
}

/** One row per signing slot: individuals and signing groups each count once. */
export function uniqueSigningSlots(signers: Participation[]): Participation[] {
  const seen = new Set<string>()
  return signers.filter((p) => {
    if (!p.signing_group_id) return true
    if (seen.has(p.signing_group_id)) return false
    seen.add(p.signing_group_id)
    return true
  })
}

function groupSigner(parties: Participation[], groupID: string): Participation | undefined {
  return parties.find((p) => p.signing_group_id === groupID && statusOf(p) === 'signed')
}

export function slotSigned(p: Participation, parties: Participation[]): boolean {
  if (p.signing_group_id) {
    return groupSigner(parties, p.signing_group_id) !== undefined
  }
  return statusOf(p) === 'signed'
}

/**
 * One Progress row: whether the slot is signed or declined, and its word.
 *
 * A signing group's row is its first member's, and the word read that
 * member's own state. When another member signed for the group, the row had
 * the check mark and "Landlords, signed by Bea Smith", then "waiting", or
 * "withdrawn" once the document was withdrawn, and still "waiting" on a
 * finished document; when a member declined, the row read "closed" and the
 * card named nobody as having declined (review 6, web-4). The date now comes
 * from whoever signed for the slot, and a member's decline is the slot's,
 * named, as one decline closes the document.
 */
export function slotProgress(
  r: Participation,
  parties: Participation[],
  envelopeStatus: string,
): { mark: 'signed' | 'declined' | null; word: string } {
  if (slotSigned(r, parties)) {
    const filler = r.signing_group_id ? groupSigner(parties, r.signing_group_id) : r
    const at = filler?.state?.signed_at
    return { mark: 'signed', word: at ? `signed ${dateTime(at)}` : 'signed' }
  }
  if (r.signing_group_id) {
    const decliner = parties.find((p) => p.signing_group_id === r.signing_group_id && statusOf(p) === 'declined')
    if (decliner) return { mark: 'declined', word: `declined by ${decliner.name}` }
  } else if (statusOf(r) === 'declined') {
    return { mark: 'declined', word: 'declined' }
  }
  return { mark: null, word: endedWord(envelopeStatus) ?? 'waiting' }
}

function slotStatus(p: Participation, parties: Participation[]): ParticipationStatus {
  if (p.signing_group_id) {
    const filler = groupSigner(parties, p.signing_group_id)
    if (filler) return 'signed'
  }
  return statusOf(p)
}

function recipientLabel(p: Participation, parties: Participation[]): string {
  if (p.signing_group_label) {
    const filler = p.signing_group_id ? groupSigner(parties, p.signing_group_id) : undefined
    if (filler && filler.id !== p.id) {
      return `${p.signing_group_label}, signed by ${filler.name}`
    }
    return p.signing_group_label
  }
  return p.name
}

/** Whether this participant is finished with the envelope, either way. */
export function isClosed(p: Participation, parties: Participation[] = []): boolean {
  const status = slotStatus(p, parties)
  if (p.role === 'needs_to_view') {
    return status === 'viewed'
  }
  if (p.role === 'cc') {
    return status === 'viewed' || status === 'signed'
  }
  if (p.signing_group_id) {
    return slotSigned(p, parties) || status === 'declined'
  }
  return status === 'signed' || status === 'declined'
}

function roleLabel(role: string): string {
  switch (role) {
    case 'cc': return 'Receives a copy'
    case 'needs_to_view': return 'Needs to view'
    case 'witness': return 'Witness'
    default: return 'Signer'
  }
}

function linkDeliveryLabel(delivery: Delivery): string {
  return delivery === 'emailed_by_docsdone' ? 'Emailed' : 'Handed to you'
}

/** Line 2 — how the link went out. */
function recipientDeliveryLine(p: Participation): string | null {
  if (p.link) return linkDeliveryLabel(p.link.delivery)
  return null
}

/**
 * Whether the sender can chase this person from here: the envelope is
 * waiting on them and docsdone has a link of its own to send again.
 */
function canRemind(p: Participation): boolean {
  return p.waiting_on && !!p.reminders && !p.reminders.why_not
}

/**
 * After the expiry, for somebody the envelope is waiting on: how the chasing
 * stands. One line for what last happened and one for what happens next, or
 * one line for why docsdone cannot do it. Each line is short enough to stand
 * on its own, because a sentence the rail had to break was broken at the
 * separator, and a rail that only said "waiting" left the sender to work out
 * whether anybody had done anything about it.
 */
function recipientReminderLines(p: Participation): string[] {
  if (!p.waiting_on || !p.reminders) return []
  const r = p.reminders
  switch (r.why_not) {
    case 'no_emailed_link':
      return ['Handed on, so no reminders']
    case 'link_bounced':
      return ['Address refused the link']
    case 'link_expired':
      return ['Link expired']
    case undefined:
    case '':
      break
    default:
      // A reason this build does not know. The server said chasing is off
      // the table, and a row that went quiet about it would leave the sender
      // wondering where the button went.
      return ['No reminders for now']
  }
  const last = r.last_at ? `Reminded ${relativeDays(r.last_at)}` : 'Not reminded yet'
  if (r.next_at) return [last, `Next ${relativeDays(r.next_at)}`]
  if (r.sent > 0) return [last, 'No more automatic reminders']
  return [last, 'Automatic reminders off']
}

/**
 * After the delivery line: when they finished, or when the link runs out.
 *
 * On a document that ended without everyone (endedWord) the link's expiry is
 * not said. A decline revokes no other link, so a signer who had not acted
 * read CLOSED above "Expires in 25 days", as if they held a live link to a
 * document that will never finish (review 6, app request 1). The app's
 * whenLine (mobile envelope model) is this rule.
 */
export function recipientExecutedOrExpired(p: Participation, parties: Participation[], envelopeStatus: string): string | null {
  const status = slotStatus(p, parties)

  if (status === 'signed') {
    if (p.state?.signed_at) return dateTime(p.state.signed_at)
    return null
  }
  if (status === 'declined') {
    if (p.state?.declined_at) return dateTime(p.state.declined_at)
    return null
  }
  if (p.link?.revoked) return 'Link withdrawn'
  if (p.link && !isClosed(p, parties) && endedWord(envelopeStatus) === null) {
    return `Expires ${relativeDays(p.link.expires_at)}`
  }
  if (status === 'viewed' && p.state?.viewed_at) {
    return dateTime(p.state.viewed_at)
  }
  return null
}

function RecipientStatusRail({
  p, parties, skipped, envelopeStatus, reissuing, reissueError, reissued, onReissue,
  reminding, remindError, reminded, onRemind,
}: {
  p: Participation
  parties: Participation[]
  skipped: boolean
  envelopeStatus: string
  reissuing: boolean
  reissueError?: string
  reissued?: ReissueResult
  onReissue: () => void
  reminding: boolean
  remindError?: string
  reminded?: RemindResult
  onRemind: () => void
}) {
  const status = slotStatus(p, parties)
  const showReissue = !skipped && canReissue(p, isClosed(p, parties), envelopeStatus)
  const showRemind = !skipped && canRemind(p)
  const delivery = recipientDeliveryLine(p)
  const executedOrExpired = recipientExecutedOrExpired(p, parties, envelopeStatus)
  const reminderLines = skipped ? [] : recipientReminderLines(p)

  return (
    <div className="recipient-status">
      {skipped ? (
        <span className="badge badge-voided">skipped</span>
      ) : (
        <RecipientBadge status={status} envelopeStatus={envelopeStatus} />
      )}
      {!skipped && delivery && (
        <div className="recipient-status-line">{delivery}</div>
      )}
      {!skipped && executedOrExpired && (
        <div className="recipient-status-when">{executedOrExpired}</div>
      )}
      {reminderLines.map((line) => (
        <div key={line} className="recipient-status-when">{line}</div>
      ))}
      {(showRemind || showReissue) && (
        <div className="recipient-actions">
          {showRemind && (
            <button
              id={`remind-row-${p.id}`}
              className="btn btn-small"
              disabled={reminding}
              aria-label={`Remind ${p.name} now`}
              onClick={onRemind}
            >
              {reminding ? 'Sending…' : 'Remind now'}
            </button>
          )}
          {showReissue && (
            <button
              className="btn btn-secondary btn-small"
              disabled={reissuing}
              aria-label={`Reissue the link for ${p.name}`}
              onClick={onReissue}
            >
              {reissuing ? 'Reissuing…' : 'Reissue link'}
            </button>
          )}
        </div>
      )}
      {/* Announced, because the button gives no other sign that anything
          happened: the rail's own lines change only once the page reloads. */}
      {remindError && (
        <div role="status" className="small" style={{ color: 'var(--bad)' }}>{remindError}</div>
      )}
      {reminded && (
        <div role="status" className={`small reminder-sent${reminded.delivered ? '' : ' is-refused'}`}>
          {reminded.delivered ? `Reminder sent to ${reminded.sent_to}` : `${reminded.sent_to} refused the reminder`}
        </div>
      )}
      {reissueError && (
        <div role="status" className="small" style={{ color: 'var(--bad)' }}>{reissueError}</div>
      )}
      {/* A link reissued before the envelope ended was revoked with it. */}
      {reissued && envelopeStatus === 'sent' && <ReissuedLink result={reissued} />}
    </div>
  )
}

/**
 * The recipient badge's ink and word. On a document that ended without
 * everyone, a person who neither signed nor declined is no longer pending:
 * the badge said PENDING beside a Progress row that said "withdrawn" or
 * "closed" (R7-web, found, not fixed; review 6). "pending" becomes the
 * Progress row's word (endedWord), and "viewed" keeps its word, each in no
 * state's ink, since nothing is in flight any more. The app's recipientBadge
 * (mobile envelope model) is this rule, word for word.
 */
export function recipientBadge(status: ParticipationStatus, envelopeStatus: string): { ink: string; label: string } {
  const ended = endedWord(envelopeStatus)
  if (ended && status !== 'signed' && status !== 'declined') return { ink: 'voided', label: status === 'pending' ? ended : status }
  return { ink: badgeFor(status), label: status }
}

function RecipientBadge({ status, envelopeStatus }: { status: ParticipationStatus; envelopeStatus: string }) {
  const b = recipientBadge(status, envelopeStatus)
  return <span className={`badge badge-${b.ink}`}>{b.label}</span>
}

/** Which badge colour a participation status borrows. */
function badgeFor(status: ParticipationStatus): string {
  if (status === 'signed') return 'completed'
  if (status === 'declined') return 'declined'
  return 'sent'
}

function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'
  return `${n}${suffix}`
}

function progressHeadline(
  status: string,
  signed: boolean,
  blocking: Participation | undefined,
  ordered: boolean,
): string {
  if (signed || status === 'completed') return 'Everyone has signed'
  if (status === 'declined') return 'Someone declined to sign'
  if (status === 'voided') return 'Withdrawn'
  if (blocking) {
    if (blocking.role === 'needs_to_view') {
      return `Waiting for ${blocking.name} to view`
    }
    const order = ordered && (blocking.role === 'signer' || blocking.role === 'witness')
      ? ` (${ordinal(blocking.routing_order)} in order)`
      : ''
    return `Waiting for ${blocking.name} to sign${order}`
  }
  return 'Out for signatures'
}

/** Which document a box is on, as a prefix, or nothing when there is one. */
function documentOf(documents: EnvelopeDocument[], itemID: string, title: string): string {
  if (documents.length <= 1) return ''
  const at = documents.find((d) => d.id === itemID)
  if (!at) return ''
  return `${at.position === 0 ? title : at.name}, `
}

/**
 * Plain-language rows derived from the evidence chain, newest first. The
 * roster is for the rows that are ABOUT a person rather than BY one: a
 * reminder names its recipient only by participation id.
 */
export function activityFeed(
  audit: AuditEvent[],
  parties: Participation[] = [],
): { seq: number; title: string; detail?: string; occurred_at: string; sealed?: true }[] {
  const rows: { seq: number; title: string; detail?: string; occurred_at: string; sealed?: true }[] = []
  const names = new Map(parties.map((p) => [p.id, p.name]))
  for (const ev of audit) {
    const row = activityRow(ev, names)
    if (row) rows.push({ seq: ev.seq, occurred_at: ev.occurred_at, ...row, ...(sealsTheDocument(ev.type) ? { sealed: true as const } : {}) })
  }
  return rows.reverse()
}

/**
 * The events that seal: the only ones whose mark in a list is gold, because
 * gold says sealed and nothing else (docs/design-system.md).
 */
export function sealsTheDocument(type: string): boolean {
  return type === 'document.sealed' || type === 'envelope.completed' || type === 'certificate.issued'
}

function activityRow(ev: AuditEvent, names: Map<string, string>): { title: string; detail?: string } | null {
  const who = ev.name_presented ?? 'Somebody'
  switch (ev.type) {
    case 'envelope.created':
      return { title: 'Envelope created', detail: ev.name_presented ? `By ${ev.name_presented}` : undefined }
    case 'envelope.sent':
      return { title: 'Sent for signature' }
    case 'envelope.voided':
      return { title: 'Withdrawn', detail: who }
    case 'reminder.sent': {
      const about = names.get(String(ev.data?.participation_id ?? '')) ?? 'the signer'
      if (ev.kind === 'system') return { title: `Reminded ${about}`, detail: 'Automatic' }
      return { title: `Reminded ${about}`, detail: `By ${who}` }
    }
    case 'participant.viewed':
      return { title: `${who} opened the document` }
    case 'participant.consented':
      return { title: `${who} agreed to sign electronically` }
    case 'participant.signed':
      return { title: `${who} signed` }
    case 'participant.declined':
      return { title: `${who} declined` }
    case 'participant.authenticated':
      return null
    default:
      return { title: eventLabel(ev.type), detail: ev.name_presented ? who : undefined }
  }
}

/**
 * What a reissue came to.
 *
 * Three outcomes and they are not interchangeable. A link handed back comes
 * with the URL, because the sender now has to deliver it. One docsdone emailed
 * says where it went, so the sender knows the address it used was the one they
 * meant. And a send that the provider refused says so plainly rather than
 * looking like success — the old link is already withdrawn either way, so a
 * sender who thinks the new one arrived has left the signer with nothing.
 */
function ReissuedLink({ result }: { result: ReissueResult }) {
  const [copied, setCopied] = useState(false)
  if (result.signing_url) {
    const id = `reissued-${result.participation_id}`
    return (
      <div className="small" style={{ marginTop: '.4rem', textAlign: 'left' }}>
        {/*
          The same three pieces the other two link fields have: a label bound to
          the input, type="text" so the app's own styling applies rather than
          the browser's, and a button, because a field captioned "to hand on
          yourself" that cannot be copied is an instruction with no way to
          follow it. Without the type the global input rule does not match, and
          a 172px unstyled box widens this table past the viewport.
        */}
        <label htmlFor={id} className="faint" style={{ marginBottom: '.2rem' }}>
          New link, to hand on yourself
        </label>
        <div className="copy-field">
          <input
            id={id}
            type="text"
            readOnly
            value={result.signing_url}
            onFocus={(ev) => ev.currentTarget.select()}
          />
          <button
            className="btn btn-secondary btn-small"
            onClick={() => {
              void navigator.clipboard.writeText(result.signing_url!)
              setCopied(true)
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    )
  }
  if (result.delivered === false) {
    return (
      <div className="small" style={{ color: 'var(--bad)', marginTop: '.4rem' }}>
        The old link is withdrawn, and the new one could not be delivered
        {result.sent_to ? ` to ${result.sent_to}` : ''}. Try again.
      </div>
    )
  }
  return (
    <div className="small faint" style={{ marginTop: '.4rem' }}>
      A new link was sent to {result.sent_to}.
    </div>
  )
}

/**
 * One value from an audit row's data, short enough to sit in a line.
 *
 * Nested values are the reason this exists. Interpolated into a string an
 * object becomes "[object Object]", which tells a reader less than leaving it
 * out would — and these rows are the audit trail, where a reader has come
 * specifically to see what was recorded.
 */
function eventValue(v: unknown): string {
  const text = typeof v === 'string' ? v : JSON.stringify(v)
  if (text === undefined) return ''
  return text.length > 24 ? `${text.slice(0, 20)}…` : text
}

/**
 * Withdrawing a document that is still out for signature.
 *
 * The reason is required, and it is required here rather than merely accepted:
 * it goes into the chain beside who withdrew it and how they were
 * authenticated, and it is what anybody reading the record afterwards sees.
 * "Withdrawn" on its own answers none of the questions somebody would ask.
 *
 * It is a two-tone danger control for a reason that is not drama: the links
 * stop working the moment this is confirmed, and a recipient part-way through
 * signing loses their session. Nothing is deleted — everything already
 * recorded stays exactly where it is — but it cannot be undone either, and the
 * way back is to send the document again.
 */
function Withdraw({
  id,
  title,
  onDone,
  onClose,
}: {
  id: string
  title: string
  onDone: () => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  return (
    <ConfirmSheet
      title="Withdraw this document"
      confirmLabel="Withdraw it"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={() => {
        if (!reason.trim()) {
          setError('Say why you are withdrawing it. The reason goes into the record.')
          return
        }
        setBusy(true)
        setError('')
        voidEnvelope(id, reason.trim())
          .then(onDone)
          .catch((e) => setError(e instanceof Error ? e.message : 'Could not withdraw it.'))
          .finally(() => setBusy(false))
      }}
    >
      <p>
        Signing links for <strong>{title}</strong> stop working immediately, including for anyone
        signing now. The existing record is kept, and this reason is added to it.
      </p>
      <p>This cannot be undone. To ask for signatures again, send the document again.</p>
      <label htmlFor="withdraw-reason">Why are you withdrawing it?</label>
      <input
        id="withdraw-reason"
        type="text"
        autoFocus
        placeholder="Sent the wrong version"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <p className="small faint">
        This is written into the record beside your name and stays there.
      </p>
    </ConfirmSheet>
  )
}
